import styled from "@emotion/styled";
import { Link, Typography } from "@kaltura/ds-react-components";
import {
    formattedNumber,
    stripTags,
    translate,
    useGuessedTimeZone,
    useTranslateTime,
} from "@kaltura/mediaspace-shared-utils";
import { Separator, Statistics } from "./StyleComponents";
import { KmsTypeMediaSchedulingData, StatisticsIndicatorType } from "@kaltura/mediaspace-shared-types";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { MediaSchedule } from "@kaltura/mediaspace-shared-ui";

export interface TitleProps {
    entryName: string;
    showCreatedByIndicator: boolean;
    createdByName?: string;
    createdByLink?: string;
    createdAt: number;
    schedulingData?: KmsTypeMediaSchedulingData;
    views?: number;
    statisticsIndicator?: StatisticsIndicatorType;
}

const className = "kms-ds-media-page-title";

const StyledTypography = styled(Typography)({
    wordBreak: "break-word",
});

/**
 * media page Title, created by+at, scheduling, and views
 */
export const Title = (props: TitleProps) => {
    const {
        entryName = "",
        showCreatedByIndicator = false,
        createdByName,
        createdByLink,
        createdAt,
        schedulingData,
        views = 0,
        statisticsIndicator,
    } = props;

    const context = useConfig();
    const tz = useGuessedTimeZone();
    const { dateFormats, isKAF } = context.application;

    const createdAtFormat = dateFormats.shortDate;
    const formattedCreatedAt = useTranslateTime(createdAt, createdAtFormat, tz);

    const showCreatedBy = showCreatedByIndicator && createdByName && createdByLink;

    const sendButtonAnalytics = useButtonAnalytics();

    const onLinkClick = () => {
        sendButtonAnalytics("Created By - Search", ButtonClickAnalyticsType.SEARCH);
    };

    const formatStatistics = () => {
        let result: string;

        if (statisticsIndicator && statisticsIndicator === StatisticsIndicatorType.PLAYS) {
            result = views === 1 ? translate("%1 Play", [1]) : translate("%1 Plays", [formattedNumber(views ?? 0)]);
        }
        else {
            result = views === 1 ? translate("%1 View", [1]) : translate("%1 Views", [formattedNumber(views ?? 0)]);
        }
        return result;
    };

    return (
        <div className={`${className}`}>
            <StyledTypography variant={"h3"}>{entryName}</StyledTypography>
            <Typography variant={"body2"} component="div">
                {showCreatedBy && !schedulingData && (
                    <Statistics>
                        {createdByName && (
                            <>
                                {translate("By")}
                                &nbsp;
                                {!isKAF && (
                                    <Link href={createdByLink} onClick={onLinkClick} size={"small"}>
                                        {stripTags(createdByName)}
                                    </Link>
                                )}
                                {isKAF && <span>{stripTags(createdByName)}</span>}
                                <Separator />
                            </>
                        )}
                        {formattedCreatedAt}
                        {views > 0 && statisticsIndicator && (
                            <>
                                <Separator />
                                {formatStatistics()}
                            </>
                        )}
                    </Statistics>
                )}
                {schedulingData && <MediaSchedule schedulingData={schedulingData} />}
            </Typography>
        </div>
    );
};
